import styled from "styled-components";
import { Link } from "react-router-dom";
import { borderRadius, spacing } from "@styling/sizing";
import { fontSizes } from "@styling/fonts";
import colors from "@styling/colors";

const Row = styled.div<{ $compact?: boolean }>`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${({ $compact }) => ($compact ? spacing.x1 : spacing.x2)}px;
`;

const Content = styled.div`
  flex: 1;
  min-width: 0;
  gap: ${spacing.x1}px;
  display: flex;
  flex-direction: column;
`;

const ItemLink = styled(Link)<{ $compact?: boolean; $inactive?: boolean }>`
  display: block;
  text-decoration: none;
  color: inherit;
  cursor: pointer;
  border: 1px solid transparent;
  border-radius: ${borderRadius.regular}px;
  padding: ${({ $compact }) => ($compact ? `${spacing.x1}px` : `${spacing.x2}px`)};
  background-color: ${colors.background};
  opacity: ${({ $inactive }) => ($inactive ? 0.55 : 1)};
  transition: background-color 0.15s ease, border-color 0.15s ease;

  &:hover {
    background-color: ${({ $inactive }) =>
      $inactive ? colors.backgroundDark : colors.backgroundLight};
    border-color: ${({ $inactive }) => ($inactive ? "transparent" : colors.border)};
  }
`;

const Title = styled.h2<{ $compact?: boolean }>`
  font-size: ${({ $compact }) =>
    $compact ? `${fontSizes.regular}px` : `${fontSizes.large}px`};
  font-weight: 600;
  color: ${colors.text};
  line-height: ${({ $compact }) => ($compact ? 1.25 : 1.3)};
`;

const SubTitle = styled.p`
  font-size: ${fontSizes.small}px;
  color: ${colors.textMuted};
  margin: 0;
  line-height: 1.25;
`;

const Trailing = styled.span`
  flex-shrink: 0;
  font-size: ${fontSizes.small}px;
  font-weight: 500;
  color: ${colors.textMuted};
  white-space: nowrap;
  line-height: 1.25;
`;

export type ListItemProps = {
  to: string;
  title: string;
  subTitle?: string;
  trailing?: string;
  actions?: React.ReactNode;
  compact?: boolean;
  inactive?: boolean;
};

export const ListItem = ({
  to,
  title,
  subTitle,
  trailing,
  actions,
  compact,
  inactive,
}: ListItemProps) => {
  return (
    <ItemLink to={to} $compact={compact} $inactive={inactive}>
      <Row $compact={compact}>
        <Content>
          <Title $compact={compact}>{title}</Title>
          {subTitle ? <SubTitle>{subTitle}</SubTitle> : null}
        </Content>
        {trailing ? <Trailing>{trailing}</Trailing> : null}
        {actions && (
          <div onClick={(e) => e.stopPropagation()}>
            {actions}
          </div>
        )}
      </Row>
    </ItemLink>
  );
};
